import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import './App.css';
import AuthState from './context/auth/AuthState';
import StoreState from './context/store/StoreState';
import MenuState from './context/menu/MenuState';
import ProductState from './context/product/ProductState';
import SettingsState from './context/settings/SettingsState';
import setAuthToken from './utils/setAuthToken';
import PrivateRoute from './components/routing/PrivateRoute';
import CustomerPrivateRoute from './components/routing/CustomerPrivateRoute';
import SmartRedirect from './components/routing/SmartRedirect';
import InstallPWA from './components/InstallPWA';

import SettingsContext from './context/settings/settingsContext';
import AuthContext from './context/auth/authContext';
import CustomerAuthState from './context/customerAuth/CustomerAuthState';

import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';

// Lazy Loaded Pages (P3: Performans & Code Splitting İyileştirmesi)
const RegisterPage = React.lazy(() => import('./pages/RegisterPage'));
const LegalPage = React.lazy(() => import('./pages/legal/LegalPage'));
const VerifyEmailPage = React.lazy(() => import('./pages/VerifyEmailPage'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'));
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const StoreDetailPage = React.lazy(() => import('./pages/StoreDetailPage'));
const MenuDetailPage = React.lazy(() => import('./pages/MenuDetailPage'));
const CustomerMenuPage = React.lazy(() => import('./pages/CustomerMenuPage'));
const DiscoverPage = React.lazy(() => import('./pages/DiscoverPage'));
const StoreOrdersPage = React.lazy(() => import('./pages/StoreOrdersPage'));
const StoreReservationsPage = React.lazy(() => import('./pages/StoreReservationsPage'));
const CustomerDiscountsPage = React.lazy(() => import('./pages/CustomerDiscountsPage'));
const StoreSalesPage = React.lazy(() => import('./pages/StoreSalesPage'));
const CustomersPage = React.lazy(() => import('./pages/CustomersPage'));
const KitchenPage = React.lazy(() => import('./pages/KitchenPage'));
const WaiterPage = React.lazy(() => import('./pages/WaiterPage'));
const SettingsPage = React.lazy(() => import('./pages/SettingsPage'));
const IntegrationsPage = React.lazy(() => import('./pages/IntegrationsPage'));
const ErpIntegrationPage = React.lazy(() => import('./pages/ErpIntegrationPage'));
const TableReservationPage = React.lazy(() => import('./pages/TableReservationPage'));
const QrCodeGeneratorPage = React.lazy(() => import('./pages/QrCodeGeneratorPage'));
const BackupRestorePage = React.lazy(() => import('./pages/BackupRestorePage'));
const LedgerPage = React.lazy(() => import('./pages/LedgerPage'));
const ZReportPage = React.lazy(() => import('./pages/ZReportPage'));
const StoreReviewsPage = React.lazy(() => import('./pages/StoreReviewsPage'));
const BranchesPage = React.lazy(() => import('./pages/BranchesPage'));
const StaffPage = React.lazy(() => import('./pages/StaffPage'));
const CampaignsPage = React.lazy(() => import('./pages/CampaignsPage'));
const PromotionsPage = React.lazy(() => import('./pages/PromotionsPage'));
const ContactRequestsPage = React.lazy(() => import('./pages/ContactRequestsPage'));
const StockManagementPage = React.lazy(() => import('./pages/StockManagementPage'));
const InvoiceManagementPage = React.lazy(() => import('./pages/InvoiceManagementPage'));
const CariManagementPage = React.lazy(() => import('./pages/CariManagementPage'));
const CashManagementPage = React.lazy(() => import('./pages/CashManagementPage'));
const BankManagementPage = React.lazy(() => import('./pages/BankManagementPage'));
const ChequeManagementPage = React.lazy(() => import('./pages/ChequeManagementPage'));
const CreditManagementPage = React.lazy(() => import('./pages/CreditManagementPage'));
const CreditCardManagementPage = React.lazy(() => import('./pages/CreditCardManagementPage'));
const AccountingManagementPage = React.lazy(() => import('./pages/AccountingManagementPage'));

const SupportPage = React.lazy(() => import('./pages/SupportPage'));
const SubscriptionPage = React.lazy(() => import('./pages/SubscriptionPage'));
const SuperAdminPage = React.lazy(() => import('./pages/SuperAdminPage'));
const ComingSoonPage = React.lazy(() => import('./pages/ComingSoonPage'));
const FastStockCountingPage = React.lazy(() => import('./pages/FastStockCountingPage'));
const CustomerDashboardPage = React.lazy(() => import('./pages/CustomerDashboardPage'));
const CustomerLoginPage = React.lazy(() => import('./pages/CustomerLoginPage'));
const MaintenancePage = React.lazy(() => import('./pages/MaintenancePage'));
const PaymentSuccessPage = React.lazy(() => import('./pages/PaymentSuccessPage'));
const PaymentCancelPage = React.lazy(() => import('./pages/PaymentCancelPage'));
const LogsPage = React.lazy(() => import('./pages/LogsPage'));
const NotificationsPage = React.lazy(() => import('./pages/NotificationsPage'));
const AnalyticsPage = React.lazy(() => import('./pages/AnalyticsPage'));
const ApiKeysPage = React.lazy(() => import('./pages/ApiKeysPage'));
const StockFeaturePage = React.lazy(() => import('./pages/StockFeaturePage'));
const PowerBIReportsPage = React.lazy(() => import('./pages/PowerBIReportsPage'));
const FinanceToolsPage = React.lazy(() => import('./pages/FinanceToolsPage'));
const FinanceCalendarPage = React.lazy(() => import('./pages/FinanceCalendarPage'));
const AutomaticPaymentsPage = React.lazy(() => import('./pages/AutomaticPaymentsPage'));
const PosControlPage = React.lazy(() => import('./pages/PosControlPage'));
const SalesInvoiceTrackingPage = React.lazy(() => import('./pages/SalesInvoiceTrackingPage'));
const TaxSgkTrackingPage = React.lazy(() => import('./pages/TaxSgkTrackingPage'));

if (localStorage.token && localStorage.token !== 'undefined') {
  setAuthToken(localStorage.token);
} else {
  localStorage.removeItem('token');
}

// Hata Yakalayıcı Bileşeni
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMessage: '', errorStack: '' };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, errorMessage: error?.message || String(error), errorStack: error?.stack || '' };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Uygulama Hatası:', error, errorInfo);
  }

  async clearCacheAndRestart() {
    localStorage.clear();
    sessionStorage.clear();
    if ('caches' in window) {
      try {
        const keys = await caches.keys();
        await Promise.all(keys.map(key => caches.delete(key)));
      } catch (e) {}
    }
    if ('serviceWorker' in navigator) {
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map(r => r.unregister()));
      } catch (e) {}
    }
    window.location.href = '/';
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[#111418] flex items-center justify-center p-6 text-center">
          <div className="max-w-2xl w-full space-y-6">
            <div className="size-20 rounded-full bg-red-500/10 flex items-center justify-center text-red-500 mx-auto">
              <span className="material-symbols-outlined text-5xl">error</span>
            </div>
            <h1 className="text-2xl font-bold text-white">Bir Şeyler Yanlış Gitti</h1>
            {this.state.errorMessage && (
              <div style={{ background: '#1e293b', border: '1px solid #ef4444', borderRadius: 8, padding: '12px 16px', textAlign: 'left' }}>
                <div style={{ color: '#ef4444', fontWeight: 700, fontSize: 13, marginBottom: 6 }}>Hata Detayı:</div>
                <code style={{ color: '#fca5a5', fontSize: 12, wordBreak: 'break-all', whiteSpace: 'pre-wrap', display: 'block' }}>
                  {this.state.errorMessage}
                </code>
                {this.state.errorStack && (
                  <details style={{ marginTop: 8 }}>
                    <summary style={{ color: '#94a3b8', fontSize: 11, cursor: 'pointer' }}>Stack Trace</summary>
                    <code style={{ color: '#64748b', fontSize: 10, whiteSpace: 'pre-wrap', display: 'block', marginTop: 4 }}>
                      {this.state.errorStack.split('\n').slice(0, 8).join('\n')}
                    </code>
                  </details>
                )}
              </div>
            )}
            <p className="text-slate-400">
              Uygulama yüklenirken bir hata oluştu. Tarayıcı önbelleğinden kaynaklı olabilir.
            </p>
            <button
              onClick={() => this.clearCacheAndRestart()}
              className="bg-orange-500 text-white px-6 py-3 rounded-xl font-bold hover:bg-orange-600 transition-colors"
            >
              Önbelleği Temizle ve Yeniden Başlat
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const AppContent = () => {
  const { settings, loading: settingsLoading } = React.useContext(SettingsContext);
  const { user, loading: authLoading } = React.useContext(AuthContext);
  const [showTimeout, setShowTimeout] = React.useState(false);
  const location = useLocation();
  const isLoginPage = location.pathname === '/login';
  const isHomePage = location.pathname === '/';

  const loading = settingsLoading || authLoading;

  React.useEffect(() => {
    let timer;
    if (loading) {
      timer = setTimeout(() => {
        setShowTimeout(true);
      }, 8000); // 8 saniye sonra timeout göster
    } else {
      setShowTimeout(false);
    }
    return () => clearTimeout(timer);
  }, [loading]);

  React.useEffect(() => {
    if (!loading && (isLoginPage || isHomePage)) {
      try {
        const nodes = Array.from(document.querySelectorAll('*'));
        nodes.forEach(el => {
          const cs = window.getComputedStyle(el);
          if (
            cs.position === 'fixed' &&
            cs.top === '0px' && cs.left === '0px' &&
            parseInt(cs.width || '0', 10) >= window.innerWidth * 0.9 &&
            parseInt(cs.height || '0', 10) >= window.innerHeight * 0.9
          ) {
            el.style.pointerEvents = 'none';
            if (cs.backgroundColor && cs.backgroundColor !== 'rgba(0, 0, 0, 0)') {
              el.style.background = 'transparent';
            }
          }
        });
      } catch {}
    }
  }, [location.pathname, loading, isLoginPage, isHomePage]);
  
  React.useEffect(() => {
    if (isLoginPage || isHomePage) {
      document.body.classList.add('route-auth');
    } else {
      document.body.classList.remove('route-auth');
    }
  }, [isLoginPage, isHomePage]);

  if (loading) {
    if (showTimeout) {
      return (
        <div className="min-h-screen bg-[#111418] flex items-center justify-center p-6 text-center">
          <div className="max-w-md w-full space-y-6">
            <div className="size-20 rounded-full bg-orange-500/10 flex items-center justify-center text-orange-500 mx-auto">
              <span className="material-symbols-outlined text-5xl animate-spin">sync</span>
            </div>
            <h1 className="text-2xl font-bold text-white">Yükleme Beklenenden Uzun Sürüyor</h1>
            <p className="text-slate-400">
              Bağlantınız yavaş olabilir veya tarayıcı önbelleğinde bir sorun oluşmuş olabilir.
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => window.location.reload()}
                className="bg-orange-500 text-white px-6 py-3 rounded-xl font-bold hover:bg-orange-600 transition-colors"
              >
                Sayfayı Yenile
              </button>
              <button
                onClick={async () => {
                  localStorage.clear();
                  sessionStorage.clear();
                  if ('caches' in window) {
                    try {
                      const keys = await caches.keys();
                      await Promise.all(keys.map(key => caches.delete(key)));
                    } catch (e) {}
                  }
                  if ('serviceWorker' in navigator) {
                    try {
                      const registrations = await navigator.serviceWorker.getRegistrations();
                      await Promise.all(registrations.map(r => r.unregister()));
                    } catch (e) {}
                  }
                  window.location.href = '/';
                }}
                className="text-slate-500 hover:text-white text-sm transition-colors"
              >
                Önbelleği Temizle ve Zorla Yenile
              </button>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-[#111418] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="size-12 border-4 border-orange-500/20 border-t-orange-500 rounded-full animate-spin"></div>
          <p className="text-slate-400 font-medium">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (settings.maintenance_mode && !isLoginPage && (!user || user.role !== 'admin')) {
    return <MaintenancePage />;
  }

const PageLoader = () => (
    <div className="min-h-screen bg-[#0a0c10] flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="size-12 border-4 border-orange-500/20 border-t-orange-500 rounded-full animate-spin"></div>
        <p className="text-slate-400 font-medium font-display">Sayfa Yükleniyor...</p>
      </div>
    </div>
  );

  return (
    <div className="App notranslate" translate="no">
      <InstallPWA />
      {/* İleride buraya bir Navbar bileşeni gelecek */}
      <React.Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/stok-yonetimi" element={<StockFeaturePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
          <Route path="/kvkk" element={<LegalPage doc="kvkk" />} />
          <Route path="/gizlilik-politikasi" element={<LegalPage doc="gizlilik-politikasi" />} />
          <Route path="/cerez-politikasi" element={<LegalPage doc="cerez-politikasi" />} />
          <Route path="/kullanim-sartlari" element={<LegalPage doc="kullanim-sartlari" />} />
          <Route path="/mesafeli-satis-sozlesmesi" element={<LegalPage doc="mesafeli-satis-sozlesmesi" />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/dashboard" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
          <Route path="/store/:id" element={<PrivateRoute><StoreDetailPage /></PrivateRoute>} />
          <Route path="/store/:id/orders" element={<PrivateRoute><StoreOrdersPage /></PrivateRoute>} />
          <Route path="/store/:id/sales" element={<PrivateRoute><StoreSalesPage /></PrivateRoute>} />
          <Route path="/store/:id/reservations" element={<PrivateRoute><StoreReservationsPage /></PrivateRoute>} />
          <Route path="/store/:id/customers" element={<PrivateRoute><CustomersPage /></PrivateRoute>} />
          <Route path="/store/:id/customer-discounts" element={<PrivateRoute><CustomerDiscountsPage /></PrivateRoute>} />
          <Route path="/store/:id/reviews" element={<PrivateRoute><StoreReviewsPage /></PrivateRoute>} />
          <Route path="/store/:id/kitchen" element={<PrivateRoute><KitchenPage /></PrivateRoute>} />
          <Route path="/store/:id/waiter" element={<PrivateRoute><WaiterPage /></PrivateRoute>} />
          <Route path="/store/:id/analytics" element={<PrivateRoute><AnalyticsPage /></PrivateRoute>} />
          <Route path="/store/:id/menu" element={<Navigate to="/store/:id" replace />} />
          <Route path="/menu/:id" element={<PrivateRoute><MenuDetailPage /></PrivateRoute>} />
          <Route path="/settings" element={<PrivateRoute><SettingsPage /></PrivateRoute>} />
          {/* ERP Entegrasyonu — bağlantı, sorgular, alan eşleme ve depo kuralları tek sayfada */}
          <Route path="/integrations" element={<PrivateRoute><ErpIntegrationPage /></PrivateRoute>} />
          <Route path="/entegrasyonlar" element={<PrivateRoute><ErpIntegrationPage /></PrivateRoute>} />
          {/* Eski entegrasyon sayfası — webhook/REST bağlantıları için hâlâ erişilebilir */}
          <Route path="/integrations/legacy" element={<PrivateRoute><IntegrationsPage /></PrivateRoute>} />
          <Route path="/reservations" element={<PrivateRoute><TableReservationPage /></PrivateRoute>} />
          <Route path="/qr-generator" element={<PrivateRoute><QrCodeGeneratorPage /></PrivateRoute>} />
          <Route path="/backup" element={<PrivateRoute><BackupRestorePage /></PrivateRoute>} />
          <Route path="/ledger" element={<PrivateRoute><LedgerPage /></PrivateRoute>} />
          <Route path="/z-report" element={<PrivateRoute><ZReportPage /></PrivateRoute>} />
          <Route path="/branches" element={<PrivateRoute><BranchesPage /></PrivateRoute>} />
          <Route path="/staff" element={<PrivateRoute><StaffPage /></PrivateRoute>} />
          <Route path="/logs" element={<PrivateRoute><LogsPage /></PrivateRoute>} />
          <Route path="/notifications" element={<PrivateRoute><NotificationsPage /></PrivateRoute>} />
          <Route path="/campaigns" element={<PrivateRoute><CampaignsPage /></PrivateRoute>} />
          <Route path="/promotions" element={<PrivateRoute><PromotionsPage /></PrivateRoute>} />
          <Route path="/inventory" element={<PrivateRoute><StockManagementPage /></PrivateRoute>} />
          <Route path="/fatura-yonetimi" element={<PrivateRoute><InvoiceManagementPage /></PrivateRoute>} />
          <Route path="/fatura-yonetimi/masraf-merkezleri" element={<Navigate to="/muhasebe/masraf-merkezleri" replace />} />
          <Route path="/fatura-yonetimi/hizmet-kartlari" element={<PrivateRoute><InvoiceManagementPage defaultTab="services" /></PrivateRoute>} />
          <Route path="/fatura-yonetimi/ekstre" element={<PrivateRoute><InvoiceManagementPage defaultTab="statement" /></PrivateRoute>} />
          <Route path="/cari" element={<PrivateRoute><CariManagementPage /></PrivateRoute>} />
          <Route path="/cari/alicilar" element={<PrivateRoute><CariManagementPage defaultType="ALICI" pageTitle="Alıcılar" /></PrivateRoute>} />
          <Route path="/cari/saticilar" element={<PrivateRoute><CariManagementPage defaultType="SATICI" pageTitle="Satıcılar" /></PrivateRoute>} />
          <Route path="/cari/personeller" element={<PrivateRoute><CariManagementPage defaultType="PERSONEL" pageTitle="Personel Yönetimi" /></PrivateRoute>} />
          <Route path="/fast-stock-counting" element={<PrivateRoute><FastStockCountingPage /></PrivateRoute>} />
          <Route path="/finans" element={<Navigate to="/kasa" replace />} />
          <Route path="/cash-registers" element={<PrivateRoute><CashManagementPage /></PrivateRoute>} />
          <Route path="/kasa" element={<PrivateRoute><CashManagementPage /></PrivateRoute>} />
          <Route path="/kasa/kartlar" element={<PrivateRoute><CashManagementPage defaultTab="cards" /></PrivateRoute>} />
          <Route path="/kasa/sirket-kartlari" element={<PrivateRoute><CashManagementPage defaultTab="companies" /></PrivateRoute>} />
          <Route path="/kasa/pos" element={<PrivateRoute><CashManagementPage defaultTab="pos" /></PrivateRoute>} />
          <Route path="/kasa/ekstre" element={<PrivateRoute><CashManagementPage defaultTab="statement" /></PrivateRoute>} />
          <Route path="/kasa/pos-ekstresi" element={<PrivateRoute><CashManagementPage defaultTab="pos-statement" /></PrivateRoute>} />
          <Route path="/kasa/sirket-ekstresi" element={<PrivateRoute><CashManagementPage defaultTab="company-statement" /></PrivateRoute>} />
          <Route path="/kasa/adisyon" element={<PrivateRoute><CashManagementPage defaultTab="adisyon" /></PrivateRoute>} />
          <Route path="/kasa/raporlar" element={<PrivateRoute><CashManagementPage defaultTab="reports" /></PrivateRoute>} />
          <Route path="/kasa/banka" element={<PrivateRoute><BankManagementPage /></PrivateRoute>} />
          <Route path="/kasa/cek-senet" element={<PrivateRoute><ChequeManagementPage /></PrivateRoute>} />
          <Route path="/finans-araclari" element={<Navigate to="/finans-takvimi" replace />} />
          <Route path="/kasa/finans-araclari" element={<Navigate to="/finans-takvimi" replace />} />

          <Route path="/finans-takvimi" element={<PrivateRoute><FinanceCalendarPage /></PrivateRoute>} />
          <Route path="/kasa/finans-takvimi" element={<PrivateRoute><FinanceCalendarPage /></PrivateRoute>} />
          <Route path="/otomatik-odemeler" element={<PrivateRoute><AutomaticPaymentsPage /></PrivateRoute>} />
          <Route path="/kasa/otomatik-odemeler" element={<PrivateRoute><AutomaticPaymentsPage /></PrivateRoute>} />
          <Route path="/pos-kontrol" element={<PrivateRoute><PosControlPage /></PrivateRoute>} />
          <Route path="/kasa/pos-kontrol" element={<PrivateRoute><PosControlPage /></PrivateRoute>} />
          <Route path="/satis-fatura-takip" element={<PrivateRoute><SalesInvoiceTrackingPage /></PrivateRoute>} />
          <Route path="/kasa/satis-fatura-takip" element={<PrivateRoute><SalesInvoiceTrackingPage /></PrivateRoute>} />
          <Route path="/vergi-sgk-takip" element={<PrivateRoute><TaxSgkTrackingPage /></PrivateRoute>} />
          <Route path="/kasa/vergi-sgk-takip" element={<PrivateRoute><TaxSgkTrackingPage /></PrivateRoute>} />
          <Route path="/kredi-takip" element={<PrivateRoute><CreditManagementPage /></PrivateRoute>} />
          <Route path="/kasa/kredi-takip" element={<PrivateRoute><CreditManagementPage /></PrivateRoute>} />
          <Route path="/kredi-kartlari" element={<PrivateRoute><CreditCardManagementPage /></PrivateRoute>} />
          <Route path="/kasa/kredi-kartlari" element={<PrivateRoute><CreditCardManagementPage /></PrivateRoute>} />

          <Route path="/muhasebe" element={<PrivateRoute><AccountingManagementPage /></PrivateRoute>} />
          <Route path="/muhasebe/hesap-plani" element={<PrivateRoute><AccountingManagementPage defaultTab="chart" /></PrivateRoute>} />
          <Route path="/muhasebe/kdv-tanimlari" element={<PrivateRoute><AccountingManagementPage defaultTab="vat" /></PrivateRoute>} />
          <Route path="/muhasebe/fis-islemleri" element={<PrivateRoute><AccountingManagementPage defaultTab="vouchers" /></PrivateRoute>} />
          <Route path="/muhasebe/muhasebelestirme-havuzu" element={<PrivateRoute><AccountingManagementPage defaultTab="pool" /></PrivateRoute>} />
          <Route path="/muhasebe/mizan" element={<PrivateRoute><AccountingManagementPage defaultTab="mizan" /></PrivateRoute>} />
          <Route path="/muhasebe/muavin" element={<PrivateRoute><AccountingManagementPage defaultTab="muavin" /></PrivateRoute>} />
          <Route path="/muhasebe/masraf-merkezleri" element={<PrivateRoute><AccountingManagementPage defaultTab="cost-centers" /></PrivateRoute>} />
          <Route path="/muhasebe/masraf-merkezi-raporu" element={<PrivateRoute><AccountingManagementPage defaultTab="cost-center-report" /></PrivateRoute>} />
          <Route path="/powerbi-reports" element={<PrivateRoute><PowerBIReportsPage /></PrivateRoute>} />
          <Route path="/contact-requests" element={<PrivateRoute><ContactRequestsPage /></PrivateRoute>} />
          <Route path="/support" element={<PrivateRoute><SupportPage /></PrivateRoute>} />
          <Route path="/subscriptions" element={<PrivateRoute><SubscriptionPage /></PrivateRoute>} />
          <Route path="/super-admin/*" element={<PrivateRoute><SuperAdminPage /></PrivateRoute>} />
          <Route path="/m/:storeId" element={<CustomerMenuPage />} />
          <Route path="/kesfet" element={<DiscoverPage />} />
          <Route path="/kesfet/:city" element={<DiscoverPage />} />
          <Route path="/kesfet/:city/:district" element={<DiscoverPage />} />

          <Route path="/customer/login" element={<CustomerLoginPage />} />
          <Route path="/customer/dashboard" element={<CustomerPrivateRoute><CustomerDashboardPage /></CustomerPrivateRoute>} />
          <Route path="/payment/success" element={<PaymentSuccessPage />} />
          <Route path="/payment/cancel" element={<PaymentCancelPage />} />
          <Route path="/menus" element={<PrivateRoute><SmartRedirect target="menus" /></PrivateRoute>} />
          <Route path="/orders" element={<PrivateRoute><SmartRedirect target="orders" /></PrivateRoute>} />
          <Route path="/api-keys" element={<PrivateRoute><ApiKeysPage /></PrivateRoute>} />
        </Routes>
      </React.Suspense>
    </div>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || '862633939746-ad70i4fh0g0fksj3889ophlk3n7c9tmp.apps.googleusercontent.com'}>
        <AuthState>
          <CustomerAuthState>
          <StoreState>
            <MenuState>
              <ProductState>
                <SettingsState>
                  <Router>
                    <AppContent />
                  </Router>
                </SettingsState>
              </ProductState>
            </MenuState>
          </StoreState>
          </CustomerAuthState>
        </AuthState>
      </GoogleOAuthProvider>
    </ErrorBoundary>
  );
}

export default App;
